import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Download, Search, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { DonorDeliveryHistory } from "./V24Panels";

const API = process.env.REACT_APP_BACKEND_URL;

export function DonorHubPanel() {
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const reload = async (q = "") => {
    setLoading(true);
    try {
      const url = q ? `/donors?search=${encodeURIComponent(q)}` : `/donors`;
      const r = await apiClient.get(url);
      setData(r.data);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const openDonor = async (id) => {
    try {
      const r = await apiClient.get(`/donors/${id}`);
      setActive(r.data);
    } catch (e) { toast.error("Failed to load donor"); }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/donors/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `donors_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Donors exported");
    } catch (e) { toast.error("Couldn't export. Please try again."); }
  };

  return (
    <div data-testid="donor-hub-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-bold">Donor Hub</h3>
          <p className="text-xs text-slate-500">
            Donors appear only after their first successful payment.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && reload(search)}
              placeholder="Search name / email / postcode"
              className="pl-9 w-64" data-testid="donor-search" />
          </div>
          <Button onClick={() => reload(search)} variant="outline"
            data-testid="donor-search-btn">Search</Button>
          <Button onClick={exportCsv} className="bg-slate-900 hover:bg-slate-800"
            data-testid="donor-export-csv">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card><CardContent className="p-0 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : !data || data.totalDonors === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            No donors yet. Donor records appear here once a fundraiser has recorded a donor submission on one of your campaigns.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="donor-table">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase text-slate-500 border-b">
                <th className="py-2 px-3">Donor</th>
                <th className="py-2 px-3">Total</th>
                <th className="py-2 px-3">Donations</th>
                <th className="py-2 px-3">Last gift</th>
                <th className="py-2 px-3">Gift Aid</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.donors.map(d => (
                <tr key={d.id} className="border-b hover:bg-slate-50"
                  data-testid={`donor-row-${d.id}`}>
                  <td className="py-2 px-3">
                    <div className="font-medium">{d.name || "—"}</div>
                    <div className="text-xs text-slate-500">{d.email || ""}</div>
                  </td>
                  <td className="py-2 px-3 font-semibold">£{d.totalDonated.toFixed(2)}</td>
                  <td className="py-2 px-3">{d.donationCount}</td>
                  <td className="py-2 px-3 text-xs">
                    {d.lastDonationAt ? String(d.lastDonationAt).slice(0, 10) : "—"}
                  </td>
                  <td className="py-2 px-3">
                    {d.giftAidActive
                      ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      : <Badge variant="outline">No</Badge>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openDonor(d.id)}
                      data-testid={`donor-open-${d.id}`}>View</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>

      {/* Detail modal */}
      {active && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setActive(null)} data-testid="donor-detail-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{active.name || "Donor"}</h2>
              <Button variant="ghost" onClick={() => setActive(null)}
                data-testid="donor-detail-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              <div><span className="text-slate-500">Email:</span> {active.email}</div>
              <div><span className="text-slate-500">Phone:</span> {active.phone || "—"}</div>
              <div><span className="text-slate-500">Postcode:</span> {active.postcode || "—"}</div>
              <div><span className="text-slate-500">Total donated:</span> £{active.totalDonated.toFixed(2)}</div>
              <div><span className="text-slate-500">First gift:</span>{" "}
                {active.firstSucceededAt ? String(active.firstSucceededAt).slice(0, 10) : "—"}</div>
              <div><span className="text-slate-500">Last gift:</span>{" "}
                {active.lastDonationAt ? String(active.lastDonationAt).slice(0, 10) : "—"}</div>
            </div>
            <h3 className="font-semibold text-sm mb-2 mt-4">Payment instructions</h3>
            <div className="space-y-1 mb-4">
              {(active.paymentInstructions || []).slice(0, 20).map(pi => (
                <div key={pi.id} className="flex justify-between bg-slate-50 rounded p-2 text-xs"
                  data-testid={`pi-row-${pi.id}`}>
                  <span>{String(pi.createdAt || "").slice(0, 10)} · {pi.kind || pi.type || "donation"}</span>
                  <span>£{Number(pi.amount || 0).toFixed(2)} · {pi.status}</span>
                </div>
              ))}
              {(active.paymentInstructions || []).length === 0 && (
                <div className="text-slate-500 text-xs">No payment instructions</div>
              )}
            </div>
            <h3 className="font-semibold text-sm mb-2">Gift Aid declarations</h3>
            <div className="space-y-1">
              {(active.giftAidDeclarations || []).slice(0, 10).map(ga => (
                <div key={ga.id} className="flex justify-between bg-slate-50 rounded p-2 text-xs">
                  <span>{String(ga.createdAt || "").slice(0, 10)}</span>
                  <span>{ga.status}</span>
                </div>
              ))}
              {(active.giftAidDeclarations || []).length === 0 && (
                <div className="text-slate-500 text-xs">No Gift Aid declarations</div>
              )}
            </div>

            {/* V2.4 — Donor Delivery Audit Trail */}
            <DonorDeliveryHistory donorId={active.id} />
          </div>
        </div>
      )}
    </div>
  );
}
